import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { legacyApi, WorkflowPlaybookModule } from '../../services/legacyApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ModulePlaybookView } from '../../components/workflow/ModulePlaybookView';

type PlaybookState = {
    version?: string;
    generatedAt?: string;
    modules: WorkflowPlaybookModule[];
};

export function HelpOverview() {
    const [searchParams] = useSearchParams();
    const targetModule = (searchParams.get('module') || '').trim().toLowerCase();
    const [state, setState] = useState<PlaybookState>({ modules: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadPlaybooks = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await legacyApi.workflow.getPlaybooks();
            setState({
                version: res.version,
                generatedAt: res.generated_at,
                modules: res.modules || [],
            });
        } catch (err: any) {
            setError(err?.message || 'Failed to load workflow playbooks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadPlaybooks();
    }, []);

    return (
        <div className="space-y-6">
            <header className="space-y-2">
                <p className="text-sm uppercase tracking-wider text-muted-foreground">Guidance & Workflows</p>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">ERP Help Center</h1>
                <p className="text-muted-foreground max-w-3xl">
                    Dynamic workflow playbooks are loaded from backend workflow registry.
                </p>
            </header>

            <Card>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div>
                        <p className="text-sm font-semibold text-slate-900">Workflow Registry Sync</p>
                        <p className="text-xs text-slate-500">
                            Version: {state.version || '-'} | Last synced:{' '}
                            {state.generatedAt ? new Date(state.generatedAt).toLocaleString() : '-'}
                        </p>
                    </div>
                    <Button variant="outline" onClick={() => void loadPlaybooks()} disabled={loading}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </CardContent>
            </Card>

            {loading ? (
                <Card>
                    <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading workflow playbooks...
                    </CardContent>
                </Card>
            ) : null}

            {error ? (
                <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
                </Card>
            ) : null}

            <div className="grid gap-6">
                {(state.modules || [])
                    .slice()
                    .sort((a, b) => {
                        if (!targetModule) return 0;
                        const aIsTarget = a.module_key === targetModule ? 1 : 0;
                        const bIsTarget = b.module_key === targetModule ? 1 : 0;
                        return bIsTarget - aIsTarget;
                    })
                    .map((module) => (
                    <ModulePlaybookView key={module.module_key} module={module} />
                ))}
            </div>
        </div>
    );
}
